import { useLayoutEffect } from 'react';
import { hasLegacyLocalePack, translateLegacyText } from './legacy/legacyLocalePacks.js';

const TRANSLATED_ATTRIBUTES = [
  'placeholder',
  'title',
  'aria-label',
  'aria-description',
  'alt',
  'data-tooltip',
  'label',
  'value',
];
const FULLY_SKIPPED_CONTAINERS = [
  'script',
  'style',
  'code',
  'pre',
  '[translate="no"]',
  '[data-i18n-skip]',
  '[data-no-interface-translation]',
].join(', ');
const EDITABLE_CONTENT_CONTAINERS = [
  'textarea',
  '[contenteditable]:not([contenteditable="false"])',
].join(', ');
const BOUNDARY_ATTRIBUTES = [
  'translate',
  'data-i18n-skip',
  'data-no-interface-translation',
  'contenteditable',
];
const BOUNDARY_ATTRIBUTE_SET = new Set(BOUNDARY_ATTRIBUTES);
const OBSERVED_ATTRIBUTES = [...TRANSLATED_ATTRIBUTES, ...BOUNDARY_ATTRIBUTES];
const TRANSLATABLE_INPUT_TYPES = new Set(['button', 'submit', 'reset']);

const getNodeElement = (node) => (
  node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement
);

const canTranslateTextNode = (node) => {
  const element = getNodeElement(node);
  return Boolean(element)
    && !element.closest(FULLY_SKIPPED_CONTAINERS)
    && !element.closest(EDITABLE_CONTENT_CONTAINERS);
};

const canTranslateElementAttributes = (element) => {
  if (!element || element.closest(FULLY_SKIPPED_CONTAINERS)) return false;

  // Attributes such as placeholder/title describe an editable control and are
  // interface copy. Descendants inside a contenteditable region can contain
  // user-authored markup, so only the editable element itself is eligible.
  const editableContainer = element.closest(EDITABLE_CONTENT_CONTAINERS);
  return !editableContainer || editableContainer === element;
};

const canTraverseNode = (node) => {
  const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  return Boolean(element) && !element.closest(FULLY_SKIPPED_CONTAINERS);
};

const installLegacyTranslation = (root, language) => {
  if (!root || !hasLegacyLocalePack(language)) return () => {};

  const textRecords = new Map();
  const attributeRecords = new Map();

  const translateTextNode = (node) => {
    if (!canTranslateTextNode(node)) return;
    const current = node.nodeValue;
    const existing = textRecords.get(node);
    if (existing?.translated === current) return;

    const source = existing && current === existing.source ? existing.source : current;
    const translated = translateLegacyText(language, source);
    if (translated !== source) {
      textRecords.set(node, { source, translated });
      node.nodeValue = translated;
    } else if (existing && current !== existing.source) {
      textRecords.delete(node);
    }
  };

  const translateAttribute = (element, attribute) => {
    if (!canTranslateElementAttributes(element) || !element.hasAttribute(attribute)) return;
    if (attribute === 'label' && element.tagName !== 'OPTGROUP') return;
    if (attribute === 'value') {
      if (element.tagName !== 'INPUT' || !TRANSLATABLE_INPUT_TYPES.has(element.type)) return;
    }
    const current = element.getAttribute(attribute);
    const key = `${attribute}`;
    let records = attributeRecords.get(element);
    if (!records) {
      records = new Map();
      attributeRecords.set(element, records);
    }
    const existing = records.get(key);
    if (existing?.translated === current) return;

    const source = existing && current === existing.source ? existing.source : current;
    const translated = translateLegacyText(language, source);
    if (translated !== source) {
      records.set(key, { source, translated });
      element.setAttribute(attribute, translated);
    } else if (existing && current !== existing.source) {
      records.delete(key);
    }
  };

  const translateElement = (element) => {
    if (!canTranslateElementAttributes(element)) return;
    TRANSLATED_ATTRIBUTES.forEach((attribute) => translateAttribute(element, attribute));
  };

  const restoreTextNode = (node) => {
    const record = textRecords.get(node);
    if (!record) return;
    if (node.nodeValue === record.translated) node.nodeValue = record.source;
    textRecords.delete(node);
  };

  const restoreElementAttributes = (element) => {
    const records = attributeRecords.get(element);
    if (!records) return;
    records.forEach(({ source, translated }, attribute) => {
      if (element.getAttribute(attribute) === translated) {
        element.setAttribute(attribute, source);
      }
    });
    attributeRecords.delete(element);
  };

  const restoreTrackedTree = (node, { includeRootAttributes = true } = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      restoreTextNode(node);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    if (includeRootAttributes) restoreElementAttributes(node);
    const textWalker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    while (textWalker.nextNode()) restoreTextNode(textWalker.currentNode);
    node.querySelectorAll('*').forEach(restoreElementAttributes);
  };

  const translateTree = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      translateTextNode(node);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE || !canTraverseNode(node)) return;

    translateElement(node);
    const textWalker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    while (textWalker.nextNode()) translateTextNode(textWalker.currentNode);
    node.querySelectorAll('*').forEach(translateElement);
  };

  const forgetTree = (node) => {
    // Nodes moved within the document are reported as removed and added in the
    // same batch; keep their source record so Romanian can still be restored.
    if (node.isConnected) return;
    if (node.nodeType === Node.TEXT_NODE) {
      textRecords.delete(node);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    attributeRecords.delete(node);
    const textWalker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    while (textWalker.nextNode()) textRecords.delete(textWalker.currentNode);
    node.querySelectorAll('*').forEach((element) => attributeRecords.delete(element));
  };

  const handleBoundaryMutation = (element) => {
    if (!canTraverseNode(element)) {
      // A full opt-out owns both the content and its UI-looking attributes.
      restoreTrackedTree(element);
      return;
    }

    const editableContainer = element.closest(EDITABLE_CONTENT_CONTAINERS);
    if (editableContainer) {
      // Editable content is user data, while attributes on the editable control
      // itself (placeholder/title/ARIA) remain eligible interface copy.
      restoreTrackedTree(element, { includeRootAttributes: editableContainer !== element });
      if (editableContainer === element) translateElement(element);
      return;
    }

    // The boundary was removed (or explicitly disabled); translate the subtree
    // again using its restored source values.
    translateTree(element);
  };

  const handleAddedNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (canTranslateTextNode(node)) translateTextNode(node);
      else restoreTextNode(node);
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) handleBoundaryMutation(node);
  };

  translateTree(root);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'characterData') {
        translateTextNode(mutation.target);
      } else if (mutation.type === 'attributes') {
        if (BOUNDARY_ATTRIBUTE_SET.has(mutation.attributeName)) {
          handleBoundaryMutation(mutation.target);
        } else {
          translateAttribute(mutation.target, mutation.attributeName);
        }
      } else {
        mutation.removedNodes.forEach(forgetTree);
        mutation.addedNodes.forEach(handleAddedNode);
      }
    }
  });
  observer.observe(root, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: OBSERVED_ATTRIBUTES,
  });

  const originalAlert = window.alert;
  const originalConfirm = window.confirm;
  const originalPrompt = window.prompt;
  const translatedAlert = (message) => originalAlert.call(window, translateLegacyText(language, String(message)));
  const translatedConfirm = (message) => originalConfirm.call(window, translateLegacyText(language, String(message)));
  const translatedPrompt = (message, defaultValue) => originalPrompt.call(
    window,
    translateLegacyText(language, String(message)),
    defaultValue,
  );
  window.alert = translatedAlert;
  window.confirm = translatedConfirm;
  window.prompt = translatedPrompt;

  return () => {
    observer.disconnect();
    if (window.alert === translatedAlert) window.alert = originalAlert;
    if (window.confirm === translatedConfirm) window.confirm = originalConfirm;
    if (window.prompt === translatedPrompt) window.prompt = originalPrompt;

    textRecords.forEach(({ source, translated }, node) => {
      if (node.isConnected && node.nodeValue === translated) node.nodeValue = source;
    });
    attributeRecords.forEach((records, element) => {
      if (!element.isConnected) return;
      records.forEach(({ source, translated }, attribute) => {
        if (element.getAttribute(attribute) === translated) element.setAttribute(attribute, source);
      });
    });
  };
};

const LegacyInterfaceBridge = ({ language }) => {
  useLayoutEffect(() => {
    // Observe the whole document so titles and UI mounted outside #root are
    // covered as well. User-authored areas opt out via the selectors above.
    const root = document.documentElement;
    return installLegacyTranslation(root, language);
  }, [language]);

  return null;
};

export { installLegacyTranslation };
export default LegacyInterfaceBridge;
